import { Directive, OnInit, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  ColDef,
  GridOptions,
  GridApi,
  GridReadyEvent,
  GetRowIdParams,
  RowClickedEvent,
  RowClassParams,
  IRowNode,
  PostSortRowsParams
} from 'ag-grid-community';
import { Router } from '@angular/router';
import { NavigationService } from '../../../service/core/navigation.service';
import { UppComponentNames } from '../../../service/model';
import { LookupOptions } from '../../../core/util/lookup-options';
import { selectNavigation } from '../../../store/navigation/navigation-selector';

/**
 * Abstract base class for search components that use ag-grid with lookup functionality
 */
@Directive()
export abstract class AgGridSearchTableComponent implements OnInit, OnDestroy {
  lookup = false;
  lookupValues: string[] = [];
  lookupOptions!: LookupOptions;

  gridOptions: GridOptions;
  gridApi: GridApi | undefined;
  columnDefs: ColDef[] = [];

  displayHandler!: (data: unknown) => void;
  modifyHandler!: (data: unknown) => void;
  copyHandler!: (data: unknown) => void;
  deleteHandler!: (data: unknown) => void;
  showHistoryHandler: (data: unknown) => void = () => {};

  protected readonly destroy$ = new Subject<void>();

  protected readonly defaultColDef: ColDef = {
    flex: 1,
    suppressMovable: true
  };

  protected readonly agGridTheme = 'ag-theme-alpine';

  constructor(
    protected readonly store: Store,
    protected readonly router: Router,
    protected readonly navigationService: NavigationService
  ) {
    this.gridOptions = this.createGridOptionsWithLookup();
  }

  /**
   * Get the text for the "Go Back" button based on the source view
   */
  get goBackLinkText(): string {
    return this.getGoBackLinkText(this.lookupOptions?.sourceView);
  }

  ngOnInit(): void {
    this.initializeLookupFromNavigationService();
    this.setupGridContext();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handles the grid ready event
   */
  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.handleGridReadyEvent();
  }

  /**
   * Handles row selection in lookup mode via row click
   */
  handleRowSelection(id: string, name: string, selected: boolean): void {
    this.lookupValues = this.cleanLookupValues(this.lookupValues);

    if (selected) {
      this.lookupValues = this.lookupOptions.singleSelect ? [name] : [...this.lookupValues, name];
    } else {
      this.lookupValues = this.lookupValues.filter((value) => value !== id && value !== name);
    }

    if (this.gridOptions.context) {
      this.gridOptions.context.lookupValues = this.lookupValues;
    }

    this.gridApi?.redrawRows();
  }

  /**
   * Navigates back to the source view with the selected data
   */
  goBack(): void {
    if (!this.lookup) {
      return;
    }

    const lookupOptions = this.initLookupOptions(this.getSourceComponent(), this.lookupValues, this.lookupOptions);
    this.navigationService.navigate(lookupOptions);
  }

  /**
   * Updates the column definitions for lookup mode
   * Override this method in derived classes to customize column behavior
   */
  protected updateColumnDefsForLookup(): void {
    if (this.columnDefs.length > 0) {
      this.columnDefs = this.columnDefs.filter((col) => col.field !== 'actions');
    }
  }

  /**
   * Initialize lookup state from navigation service instead of continuously subscribing
   */
  private initializeLookupFromNavigationService(): void {
    this.store.pipe(select(selectNavigation), take(1)).subscribe((lookupParam) => {
      if (lookupParam) {
        this.lookupOptions = lookupParam;
        this.lookupValues = lookupParam.data || [];
        this.lookup = !!lookupParam.lookup;

        if (this.gridOptions.context) {
          this.gridOptions.context.isLookup = this.lookup;
          this.gridOptions.context.lookupValues = this.lookupValues;
          this.gridOptions.context.lookupOptions = lookupParam;
        }

        if (this.lookup) {
          this.updateColumnDefsForLookup();
        }
      }
    });
  }

  /**
   * Setup the grid context with action handlers
   */
  private setupGridContext(): void {
    if (!this.gridOptions.context) {
      this.gridOptions.context = {};
    }

    this.gridOptions.context.componentParent = this;

    // Set up handlers - these will only work if the component implements the abstract methods
    this.gridOptions.context.displayHandler = this.getDisplayHandler();
    this.gridOptions.context.modifyHandler = this.getModifyHandler();
    this.gridOptions.context.copyHandler = this.getCopyHandler();
    this.gridOptions.context.deleteHandler = this.getDeleteHandler();
    this.gridOptions.context.showHistoryHandler = this.getShowHistoryHandler();
    this.gridOptions.context.deleteSending$ = this.getDeleteSendingObservable();
    this.gridOptions.context.nameField = this.getNameField();

    this.displayHandler = this.getDisplayHandler();
    this.modifyHandler = this.getModifyHandler();
    this.copyHandler = this.getCopyHandler();
    this.deleteHandler = this.getDeleteHandler();
    this.showHistoryHandler = this.getShowHistoryHandler();
  }

  /**
   * Creates complete grid options with lookup functionality
   */
  private createGridOptionsWithLookup(): GridOptions {
    const baseOptions = this.createBaseGridOptions();

    return {
      ...baseOptions,
      onRowClicked: (event: RowClickedEvent) => this.createRowClickHandler(event),
      getRowClass: (params: RowClassParams) => this.createRowClassFunction(params)
    };
  }

  /**
   * Creates base grid options with common configuration
   */
  private createBaseGridOptions(): GridOptions {
    return {
      theme: 'legacy',
      pagination: true,
      paginationPageSize: 10,
      paginationPageSizeSelector: [10, 20, 30],
      suppressCellFocus: true,
      scrollbarWidth: 0,
      suppressColumnMoveAnimation: true,
      context: { componentParent: this },
      getRowId: (params: GetRowIdParams): string => params.data.id,
      domLayout: 'autoHeight',
      suppressHorizontalScroll: false,
      localeText: {
        pageSizeSelectorLabel: $localize`:@@upp.global.pagination.recordsPerPage:Records per page:`
      }
    };
  }

  /**
   * Creates a row click handler for lookup mode
   */
  private createRowClickHandler(event: RowClickedEvent): void {
    if (this.lookup) {
      const currentSelected = this.isRowSelected(event.data.id, event.data.name, this.lookupValues);
      this.handleRowSelection(event.data.id, event.data.name, !currentSelected);
    }
  }

  /**
   * Creates a row class function for selected rows
   */
  private createRowClassFunction(params: RowClassParams): string {
    if (this.lookup && params.data && this.isRowSelected(params.data.id, params.data.name, this.lookupValues)) {
      return 'selected-row';
    }
    return '';
  }

  /**
   * Handles the grid ready event
   */
  private handleGridReadyEvent(): void {
    if (!this.gridApi) {
      return;
    }

    if (this.lookup && this.lookupValues.length > 0) {
      if (this.gridOptions.context) {
        this.gridOptions.context.lookupValues = this.lookupValues;
      }

      this.gridApi.redrawRows();
    }
  }

  /**
   * Checks if a row is selected based on ID or name
   */
  private isRowSelected(id: string, name: string, lookupValues: string[]): boolean {
    if (
      Array.isArray(lookupValues) &&
      lookupValues.length === 1 &&
      Array.isArray(lookupValues[0]) &&
      lookupValues[0].length === 0
    ) {
      return false;
    }

    return lookupValues.includes(id) || lookupValues.includes(name);
  }

  /**
   * Initialize lookup options with cleaned values
   */
  private initLookupOptions(
    sourceComponent: UppComponentNames,
    lookupValues: string[],
    lookupOptions: LookupOptions
  ): LookupOptions {
    const cleanedValues = this.cleanLookupValues(lookupValues);

    return {
      lookup: false,
      data: cleanedValues,
      sourceComponent,
      destinationComponent: `${lookupOptions?.sourceComponent}/${lookupOptions?.sourceView}/`,
      fieldName: lookupOptions?.fieldName,
      fieldPath: lookupOptions?.fieldPath
    };
  }

  /**
   * Returns the appropriate text for the "Go Back" button based on the source view
   */
  private getGoBackLinkText(lookupSourceView: string | undefined): string {
    switch (lookupSourceView) {
      case 'create':
        return $localize`:@@upp.global.button.returnCreate:Back to create`;
      case 'modify':
        return $localize`:@@upp.global.button.returnModify:Back to modify`;
      case 'search':
        return $localize`:@@upp.global.button.returnSearch:Back to search`;
      default:
        return $localize`:@@upp.global.button.return:Back`;
    }
  }

  /**
   * Cleans up lookup values if they contain an empty array
   */
  private cleanLookupValues(lookupValues: string[]): string[] {
    if (
      Array.isArray(lookupValues) &&
      lookupValues.length === 1 &&
      Array.isArray(lookupValues[0]) &&
      lookupValues[0].length === 0
    ) {
      return [];
    }
    return lookupValues || [];
  }

  /**
   * Handles post-sort rows for backend pagination to maintain sparse array structure.
   * Only re-orders real data within the current page range while preserving placeholders.
   *
   * @param params The PostSortRowsParams from ag-grid
   * @param paginationEnabled Whether backend pagination is enabled
   * @returns True if the nodes were reorganized, false if no action was taken
   */
  protected handlePostSortRowsForPagination(params: PostSortRowsParams, paginationEnabled: boolean): boolean {
    if (!paginationEnabled || !this.gridApi) {
      return false;
    }

    // Separate real data nodes from placeholder nodes
    const realDataNodes: IRowNode[] = [];
    const placeholderMap = new Map<number, IRowNode>();

    params.nodes.forEach((node) => {
      if (node.data?.id) {
        realDataNodes.push(node);
      } else if (node.data?.__idx !== undefined) {
        placeholderMap.set(node.data.__idx, node);
      }
    });

    // If there are no placeholders, this is FE pagination fallback - no need to reorganize
    if (placeholderMap.size === 0) {
      return false;
    }

    // Get current page info to determine where to place sorted real data
    const currentPage = this.gridApi.paginationGetCurrentPage();
    const pageSize = this.gridApi.paginationGetPageSize();
    const startIdx = currentPage * pageSize;
    const endIdx = startIdx + pageSize;

    // Rebuild nodes array with sorted real data in current page range, placeholders elsewhere
    const newNodes: IRowNode[] = new Array(params.nodes.length);
    let realDataIndex = 0;

    for (let i = 0; i < params.nodes.length; i++) {
      if (i >= startIdx && i < endIdx && realDataIndex < realDataNodes.length) {
        // Current page range - use sorted real data
        newNodes[i] = realDataNodes[realDataIndex++];
      } else {
        // Outside current page or no more real data - use existing placeholder
        const placeholder = placeholderMap.get(i);
        if (placeholder) {
          newNodes[i] = placeholder;
        } else {
          // Fallback: if no placeholder exists, keep original node to avoid corruption
          newNodes[i] = params.nodes[i];
        }
      }
    }

    // Replace nodes array with reorganized nodes
    params.nodes.length = 0;
    params.nodes.push(...newNodes);
    return true;
  }

  abstract getSourceComponent(): UppComponentNames;
  abstract getDisplayHandler(): (data: unknown) => void;
  abstract getModifyHandler(): (data: unknown) => void;
  abstract getCopyHandler(): (data: unknown) => void;
  abstract getDeleteHandler(): (data: unknown) => void;
  abstract getShowHistoryHandler(): (data: unknown) => void;
  abstract getDeleteSendingObservable(): Observable<boolean>;
  abstract getNameField(): string;
}
